import { API_CONFIG } from './config';
import { httpService } from './http';
import { isCanonicalUuidV4, normalizeCanonicalUuidV4 } from '../../utils/uuid';

const SHA256_HEX_PATTERN = /^[a-f0-9]{64}$/;

function bytesToUuidV4(bytes) {
    const buffer = new Uint8Array(bytes);
    buffer[6] = (buffer[6] & 0x0f) | 0x40;
    buffer[8] = (buffer[8] & 0x3f) | 0x80;
    const hex = Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createBillingIdempotencyKey() {
    const cryptoApi = window.crypto;
    if (typeof cryptoApi?.randomUUID === 'function') {
        const generatedValue = cryptoApi.randomUUID();
        if (typeof generatedValue !== 'string') {
            throw new Error('Browser crypto generated a non-canonical UUIDv4 idempotency key.');
        }
        const value = generatedValue.toLowerCase();
        if (isCanonicalUuidV4(value)) {
            return value;
        }
        throw new Error('Browser crypto generated a non-canonical UUIDv4 idempotency key.');
    }
    if (typeof cryptoApi?.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        cryptoApi.getRandomValues(bytes);
        return bytesToUuidV4(bytes);
    }
    throw new Error('Secure browser crypto is required for billing mutations.');
}

function billingMutationOptions(options = {}) {
    const { idempotencyKey, headers, ...requestOptions } = options;
    const resolvedIdempotencyKey = idempotencyKey || createBillingIdempotencyKey();
    if (!isCanonicalUuidV4(String(resolvedIdempotencyKey || '').toLowerCase())) {
        throw new Error('A canonical UUIDv4 Idempotency-Key is required for billing mutations.');
    }
    return {
        ...requestOptions,
        headers: {
            ...(headers || {}),
            'Idempotency-Key': String(resolvedIdempotencyKey).toLowerCase(),
        },
    };
}

function requireCanonicalUuidV4(value, label) {
    const normalized = normalizeCanonicalUuidV4(value);
    if (!normalized) {
        throw new Error(`A canonical UUIDv4 ${label} is required for admin billing mutations.`);
    }
    return normalized;
}

function requireSha256Hex(value, label) {
    const normalized = String(value || '').trim().toLowerCase();
    if (!SHA256_HEX_PATTERN.test(normalized)) {
        throw new Error(`A SHA-256 hex ${label} is required for admin billing mutations.`);
    }
    return normalized;
}

function requireNonBlank(value, label) {
    if (typeof value !== 'string' || value.trim() === '') {
        throw new Error(`${label} is required for admin billing mutations.`);
    }
    return value.trim();
}

export async function fetchPublicBillingPlans(options = {}) {
    return httpService.get(API_CONFIG.ENDPOINTS.BILLING.PLANS, {
        ...options,
        credentials: 'omit',
        skipAuthHeader: true,
        skipAuthRefresh: true,
    });
}

export async function fetchCurrentBillingSubscription(options = {}) {
    return httpService.get(API_CONFIG.ENDPOINTS.BILLING.SUBSCRIPTION, options);
}

export async function createCheckoutSession(billingPriceId, options = {}) {
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.CHECKOUT_SESSIONS,
        { billingPriceId },
        billingMutationOptions(options),
    );
}

export async function createPortalSession(options = {}) {
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.PORTAL_SESSIONS,
        {},
        billingMutationOptions(options),
    );
}

export async function cancelSubscription(options = {}) {
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.SUBSCRIPTION_CANCEL,
        {},
        billingMutationOptions(options),
    );
}

export async function resumeSubscription(options = {}) {
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.SUBSCRIPTION_RESUME,
        {},
        billingMutationOptions(options),
    );
}

export async function fetchAdminBillingPlans(options = {}) {
    return httpService.get(API_CONFIG.ENDPOINTS.BILLING.ADMIN_PLANS, options);
}

export async function fetchAdminPlatinumGrants(options = {}) {
    return httpService.get(API_CONFIG.ENDPOINTS.BILLING.ADMIN_PLATINUM_GRANTS, options);
}

export async function validateAdminPlanVersion(planVersionId, options = {}) {
    const resolvedPlanVersionId = requireCanonicalUuidV4(planVersionId, 'plan version ID');
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.ADMIN_PLAN_VERSION_VALIDATE(resolvedPlanVersionId),
        {},
        options,
    );
}

export async function previewAdminPlanVersionImpact(planVersionId, options = {}) {
    const resolvedPlanVersionId = requireCanonicalUuidV4(planVersionId, 'plan version ID');
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.ADMIN_PLAN_VERSION_IMPACT_PREVIEW(resolvedPlanVersionId),
        {},
        options,
    );
}

export async function fetchAdminReconciliationRun(runId, options = {}) {
    const resolvedRunId = requireCanonicalUuidV4(runId, 'reconciliation run ID');
    return httpService.get(API_CONFIG.ENDPOINTS.BILLING.ADMIN_RECONCILIATION_RUN(resolvedRunId), options);
}

function adminActionGrantMutationOptions({
    idempotencyKey,
    sessionFamilyDigest,
    challengeId,
    actionGrant,
    ...options
} = {}) {
    const resolvedIdempotencyKey = requireCanonicalUuidV4(
        idempotencyKey || createBillingIdempotencyKey(),
        'Idempotency-Key',
    );
    const resolvedSessionFamilyDigest = requireSha256Hex(
        sessionFamilyDigest,
        'X-Billing-Session-Family-Digest',
    );
    const resolvedChallengeId = requireCanonicalUuidV4(
        challengeId,
        'X-Billing-Action-Grant-Challenge-Id',
    );
    const resolvedActionGrant = requireNonBlank(
        actionGrant,
        'X-Billing-Action-Grant',
    );
    return {
        ...options,
        headers: {
            ...(options.headers || {}),
            'Idempotency-Key': resolvedIdempotencyKey,
            'X-Billing-Session-Family-Digest': resolvedSessionFamilyDigest,
            'X-Billing-Action-Grant-Challenge-Id': resolvedChallengeId,
            'X-Billing-Action-Grant': resolvedActionGrant,
        },
    };
}

export async function startAdminReconciliationRun(request, options = {}) {
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.ADMIN_RECONCILIATION_RUNS,
        request || {},
        adminActionGrantMutationOptions(options),
    );
}

export async function grantAdminPlatinum(request, options = {}) {
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.ADMIN_PLATINUM_GRANTS,
        request,
        adminActionGrantMutationOptions(options),
    );
}

export async function revokeAdminPlatinum(userId, request, options = {}) {
    const resolvedUserId = requireCanonicalUuidV4(userId, 'userId');
    return httpService.post(
        API_CONFIG.ENDPOINTS.BILLING.ADMIN_PLATINUM_GRANT_REVOKE(resolvedUserId),
        request,
        adminActionGrantMutationOptions(options),
    );
}

export async function completeBillingActionGrantPassword(request, options = {}) {
    return httpService.post(
        API_CONFIG.ENDPOINTS.AUTH_BILLING_ACTION_GRANTS.PASSWORD_COMPLETE,
        request,
        options,
    );
}

export function redirectToHostedBillingUrl(hostedUrl) {
    if (typeof hostedUrl !== 'string') {
        throw new Error('Billing provider returned an invalid hosted URL.');
    }
    let url;
    try {
        url = new URL(hostedUrl);
    } catch (_) {
        throw new Error('Billing provider returned an invalid hosted URL.');
    }
    if (url.protocol !== 'https:' || url.username || url.password) {
        throw new Error('Billing provider returned an invalid hosted URL.');
    }
    window.location.assign(url.toString());
}

const billingApi = {
    fetchPublicBillingPlans,
    fetchCurrentBillingSubscription,
    createCheckoutSession,
    createPortalSession,
    cancelSubscription,
    resumeSubscription,
    fetchAdminBillingPlans,
    fetchAdminPlatinumGrants,
    validateAdminPlanVersion,
    previewAdminPlanVersionImpact,
    fetchAdminReconciliationRun,
    startAdminReconciliationRun,
    grantAdminPlatinum,
    revokeAdminPlatinum,
    completeBillingActionGrantPassword,
    redirectToHostedBillingUrl,
    createBillingIdempotencyKey,
};

export default billingApi;
